import { useEffect, useRef } from "react";

/**
 * AUTO-SCROLL for the Infinite Scroll (2026-09-14): after `sec` seconds on a wall, page
 * to the next one — the same `step(1)` the Arrow key makes, so everything that already
 * follows a page change (the observer writing `active`, `infinity_page`, the quest bump,
 * the "each wall arrives silent" reset, the lead-page deal) follows this one too.
 *
 * THE CLOCK RESTARTS ON EVERY WALL. `active` is a dependency, so a swipe, a wheel or a
 * key that lands on a new wall re-arms from a fresh wait (owner: a manual swipe restarts
 * the clock rather than turning auto-scroll off).
 *
 * WHEN IT HOLDS STILL. Nothing is armed while the screen is paused (the remote's pause,
 * a StageProps field) or the wait is 0. When the clock fires under a sheet or a dialog
 * (`[role="dialog"]` — the settings, the guide, the sign-in, a buy dialog: the gate the
 * arrow keys already keep), or with the tab hidden, it re-arms for another wait rather
 * than advancing — a wall must never change under a sheet, and a backgrounded tab must
 * not scroll a hundred walls nobody saw. Coming back to a visible tab starts a fresh
 * wait, not a stale deadline. A firing always re-arms: a step that clamped at the last
 * dealt wall tries again after another wait rather than falling silent.
 */
export function useAutoScroll({
  sec,
  active,
  paused,
  step,
  onAuto,
}: {
  /** The wait, seconds; 0 = off. */
  sec: number;
  /** The wall on the glass — a change restarts the clock. */
  active: number;
  paused: boolean;
  step: (dir: 1 | -1) => void;
  /** Called right before an automatic advance, so the page event can say `auto`. */
  onAuto: () => void;
}): void {
  const onAutoRef = useRef(onAuto);
  onAutoRef.current = onAuto;
  useEffect(() => {
    if (sec === 0 || paused) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, sec * 1000);
    };
    const fire = () => {
      timer = undefined;
      if (document.visibilityState === "hidden" || document.querySelector('[role="dialog"]')) {
        arm();
        return;
      }
      onAutoRef.current();
      step(1);
      // Re-armed HERE as well as on the wall change: if the next wall is not dealt yet the
      // step clamps in place, `active` never changes, and without this the clock would go
      // quiet until a hand moved it. When the wall does turn, the change re-runs the
      // effect and replaces this timer with a fresh one.
      arm();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
    };
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [sec, active, paused, step]);
}
